/**
 * lib/useTermState.ts
 * Resolves the current term's lock state and whether the active user
 * may edit it (closed/locked => admin only). Centralises the rule so
 * every data-entry screen behaves identically.
 */
import { useMemo } from 'react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { isTermEditable } from '@/lib/permissions';
import type { TermState } from '@/types';

export function useTermState(year: string, term: string) {
  const { termStatuses } = useData();
  const { userRole } = useAuth();

  return useMemo(() => {
    const rec = termStatuses.find(
      (t) => t.year === year && t.term === term,
    );
    const state: TermState = rec?.status ?? 'open';
    const editable = isTermEditable(state, userRole);
    const monthLocks: Record<string, boolean> =
      rec?.monthLocks ?? {};
    // P6: a month is locked for the current user if the term
    // itself isn't editable for them (closed/locked => admin
    // only, unchanged) OR that month was locked within an
    // otherwise-open term. Admins keep the same override they
    // already have for term lock, so a month lock is a teacher
    // deadline, not an admin handcuff.
    const isAdmin = userRole === 'admin';
    const isMonthLocked = (month: string) => {
      if (!editable) return true; // term closed/locked for them
      if (isAdmin) return false; // admin override (as with term)
      return !!monthLocks[month];
    };
    return { state, editable, monthLocks, isMonthLocked };
  }, [termStatuses, year, term, userRole]);
}
